import { DOCUMENT } from '@angular/common';
import { HttpContext } from '@angular/common/http';
import { EnvironmentProviders, Provider, inject } from '@angular/core';
import { EgProblemService } from '@expertgroup/shell';
import { EG_SUPPORT_COPY_EN, EG_SUPPORT_PROBLEM_SOURCE, type EgSupportEntity, provideEgSupport, provideEgSupportCopy, provideEgSupportGraphql } from '@expertgroup/support';
import { CopyService } from '@/app/core/copy/copy.service';
import { Language, trenutniJezik } from '@/app/core/copy/language';
import { SKIP_ERROR_TOAST } from '@/app/core/http/error.interceptor';
import { BusinessEntityDto } from '@/app/core/models/business-entity';
import { GlobalStateService } from '@/app/core/state/global-state.service';
import { environment } from '@/environments/environment';

/**
 * Podrška (Ticketing) u ProTax-u — `@expertgroup/support` (plan podrške, slajs T7).
 *
 * Biblioteka nosi ekran, pokretač, stavku menija i GraphQL adapter; ovde je SAMO ono što zna ProTax: ime
 * aplikacije, aktivno pravno lice, adresa GraphQL-a i kako se pozadinski poll ućutkuje u interceptoru grešaka.
 *
 * ⚠️ Tenant se nigde ne šalje i ne bira. ProTax API (T6) uzima tenant iz korisnikovog tokena, a Ticketing iz
 * potpisanog OBO tokena. Pravno lice je samo KONTEKST tiketa, ne filter: TENANT pogled je ceo tenant (odluka
 * vlasnika 2, 8.10.2026), pa kolega vidi tiket i kad nema pristup tom pravnom licu.
 *
 * ⚠️ Dok T6 nije na serveru, `/graphql` ne poznaje polja podrške i HotChocolate odbija dokument sa HTTP 400 i golim
 * GraphQL `errors[]`. Strana pokazuje „Podrška trenutno nije dostupna", a pokretač ostaje bez badža (poll je tih).
 * Zahtevi koje pokreće korisnik (učitavanje strane, „Pokušaj ponovo", deep link `?requestId`) po ugovoru biblioteke
 * nisu pozadinski, pa interceptor uz to prikaže i jedan opšti toast („Došlo je do greške.") — srpski, ne sirovu
 * Angular poruku (v. `isGraphqlErrorEnvelope` u `error.interceptor.ts`). To je očekivano stanje do staging
 * cut-over-a (T8: API pa UI). Posle T6 greške podrške stižu kao 200 + `errors[]`, pa ih prikazuje samo biblioteka.
 */

/** Ruta strane podrške — jedno mesto za rutu, šinu, pokretač i stavku menija. */
export const PODRSKA_PUTANJA = '/aplikacija/podrska';

/**
 * Vrsta entiteta u kontekstu tiketa. Stabilan ASCII naziv koji vidi agent u Ticketing-u, nikad se ne prevodi —
 * isti naziv koji API koristi za pravno lice (`api/BusinessEntity`).
 */
export const PROTAX_SUPPORT_ENTITY_TYPE = 'BusinessEntity';

/**
 * Aktivno pravno lice kao entitet tiketa. Naziv je isti tekst koji korisnik vidi u izboru pravnog lica u gornjoj
 * traci (naziv · PIB) — kolege iz tenanta i agent ga čitaju; PIB razlikuje slično nazvana lica.
 */
export function protaxSupportEntity(entity: BusinessEntityDto | null, t: CopyService['t']): EgSupportEntity | null {
    if (!entity) return null;
    const pib = entity.pib?.trim();
    return {
        type: PROTAX_SUPPORT_ENTITY_TYPE,
        id: String(entity.id),
        label: pib ? t('layout.header.entityOption', { name: entity.name, pib }) : entity.name
    };
}

/**
 * Adresa Ticketing UI-ja za „Otvori u Ticketing-u" (samo pogled Dodeljeni).
 *
 * ⚠️ Adresa postoji u ljusci 0.16: javni `EG_LAUNCHER_ACCESS.load()` vraća registrovane UI klijente sa adresama
 * (aplikacija `ExpertGroup-ticketing-service`, klijent `ExpertGroup-ticketing-ui` — „jedan id, četiri uloge").
 * Prepreka je biblioteka: `EgSupportConfig.ticketingUiUrl` je sinhroni string koji fabrika pročita jednom, a
 * `load()` je Observable. Dok biblioteka ne primi funkciju/signal, važi rezerva iz plana:
 * `environment.ecosystem.ticketing`. Na stagingu je ta vrednost namerno prazna (launcher čita adrese iz Identity-ja,
 * PR 3990), pa tamo dugme „Otvori u Ticketing-u" ne postoji.
 */
export function protaxTicketingUiUrl(value: string | undefined = environment.ecosystem.ticketing): string | undefined {
    return value?.trim() || undefined;
}

/**
 * Provajderi podrške za `app.config.ts`. Bezuslovno — podrška nije kanarinac kao asistent.
 *
 * Obe fabrike se izvršavaju lenjo, kad biblioteci prvi put zatreba konfiguracija (pokretač u gornjoj traci), dakle
 * posle prijave i u kontekstu injekcije.
 */
export function protaxSupportProviders(jezik: Language = trenutniJezik()): (Provider | EnvironmentProviders)[] {
    return [
        provideEgSupport(() => {
            const t = inject(CopyService).t;
            const state = inject(GlobalStateService);
            const document = inject(DOCUMENT);
            return {
                appLabel: t('layout.brand.name'),
                entityLabel: t('layout.header.entityLabel'),
                ticketingUiUrl: protaxTicketingUiUrl(),
                // Čita se kad se otvori „Novi zahtev" — tada važi pravno lice izabrano u gornjoj traci.
                currentEntity: () => protaxSupportEntity(state.activeEntity(), t),
                // Ekran sa kog korisnik kreće; biblioteka ga pamti PRE navigacije (EgSupportOrigin.capture()).
                sourceUrl: () => document.location.href
            };
        }),
        provideEgSupportGraphql(() => ({
            // ⚠️ graphqlEndpoint, ne `apiUrl + '/graphql'`: apiUrl se završava na `/api`. Token kači postojeći
            // authInterceptor (svaki zahtev osim asistentovog) — adapter šalje kroz HttpClient, ne kroz Apollo.
            graphqlUrl: environment.graphqlEndpoint,
            // Pozadinski poll brojača ne prikazuje toast: bez ovoga bi ispad podrške bio toast na svakih 60 s.
            backgroundContext: () => new HttpContext().set(SKIP_ERROR_TOAST, true)
        })),
        // Isti servis koji puni interceptor, Apollo link i forma — dijalog „Novi zahtev" nudi grešku sa ekrana.
        // `useExisting`, ne nova instanca (isto kao EG_ASSISTANT_PROBLEM_SOURCE).
        { provide: EG_SUPPORT_PROBLEM_SOURCE, useExisting: EgProblemService },
        // Tekstovi podrške su biblioteke (srpski podrazumevano); domaćin bira samo jezik, kao za asistenta.
        ...(jezik === 'en' ? [provideEgSupportCopy(EG_SUPPORT_COPY_EN)] : [])
    ];
}
